import { IGNORED_CONTAINERS_CLASSES, PALANTIR_SCROLLBAR_OVERRIDE_VARIABLE } from './constants';

export interface ToggleWindowEventOptions {
  eventName: keyof WindowEventMap;
  handler: EventListener | ((e: KeyboardEvent) => void);
  options?: EventListenerOptions | boolean;
}

export function toggleWindowEvents(events: ToggleWindowEventOptions[], type: 'add' | 'remove') {
  for (const event of events) {
    if (type === 'add') {
      window.addEventListener(event.eventName, event.handler as EventListener, event.options);

      continue;
    }

    window.removeEventListener(event.eventName, event.handler as EventListener, event.options);
  }
}

export function checkIfBrowserSupportsPassive() {
  // https://developer.mozilla.org/en-US/docs/Web/API/EventTarget/addEventListener#safely_detecting_option_support
  let passiveSupported = false;

  try {
    const options = {
      get passive() {
        // This function will be called when the browser
        // attempts to access the passive property.
        passiveSupported = true;
        return false;
      },
    } as EventListenerOptions;

    window.addEventListener(
      'test' as unknown as keyof WindowEventMap,
      null as unknown as EventListener,
      options,
    );
    window.removeEventListener(
      'test' as unknown as keyof WindowEventMap,
      null as unknown as EventListener,
      options,
    );
  } catch {
    passiveSupported = false;
  }

  return passiveSupported;
}

export function determineScrollEventOptionsAndName(isPassiveSupported: boolean) {
  // Chrome and Firefox on Android support passive event listeners
  const scrollEventListenerOptions: EventListenerOptions | boolean = isPassiveSupported
    ? ({ passive: false } as EventListenerOptions)
    : false;

  const testDiv = document.createElement('div');
  const scrollEventName = ('onwheel' in testDiv
    ? 'wheel'
    : 'mousewheel') as unknown as keyof WindowEventMap;

  // Schedule the removal of the test div for the next tick
  setTimeout(() => {
    testDiv.remove();
  });

  return {
    scrollEventName,
    scrollEventListenerOptions,
  };
}

export function isIgnoredElement(target: EventTarget | null): boolean {
  for (const selector of IGNORED_CONTAINERS_CLASSES) {
    if (document.querySelector(selector)?.contains(target as Node)) {
      return true;
    }
  }

  return false;
}

export function overrideScrollbarOnIgnoredContainers() {
  for (const ignoredContainerClass of IGNORED_CONTAINERS_CLASSES) {
    const element = document.querySelector(ignoredContainerClass);

    if (element) {
      (element as HTMLElement).style.setProperty(PALANTIR_SCROLLBAR_OVERRIDE_VARIABLE, 'auto');
    }
  }
}
